import { CompanyBusinessMode } from '~/swagger/portal/Api';
import { IS_HOA_DEMO_MODE, toHoaHost, toPropertyManagementHost } from '~/utils/hoaDemoMode';

/**
 * The HOA and Company portals run on sibling hosts (`hoa.<domain>` /
 * `portal.<domain>`) that are SEPARATE origins with separate storage. So an
 * account signed in on one host is invisible in the other's switcher.
 *
 * To still show "your other accounts live on the other portal", each host
 * publishes a NAME-ONLY roster of its accounts (no tokens) that the sibling host
 * can read, via TWO complementary channels:
 *
 *  1. A cookie on the shared PARENT domain (`magicdoor-test.com`). Reliable on
 *     real multi-label domains and gives a "cold" experience — the other host's
 *     accounts show even before you've ever crossed over. Browsers refuse to
 *     share cookies scoped to a single-label domain (`localhost`), so this
 *     channel is simply absent in local dev.
 *  2. A localStorage MIRROR per origin, seeded by the roster the SOURCE host
 *     hands over in the cross-host navigation URL ({@link readIncomingRoster}).
 *     Origin-local, so it works identically on localhost and prod — but a host
 *     only learns the other's accounts once you've crossed over at least once.
 *
 * Reads union both channels; writes update both. Tokens never cross origins; this
 * is purely for display + a link back to the host where the session lives.
 */

const ROSTER_COOKIE = 'mdCrossHostAccounts';
const ROSTER_MIRROR_KEY = 'crossHostAccountsMirror';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
/** Fragment key the source host uses to hand its roster to the destination host. */
const ROSTER_PARAM = 'roster';

export type CrossHostAccount = {
  /** Composite account id (`<sub>:<company_id>`) on its own host. */
  id: string;
  companyId: string;
  label: string;
  subLabel: string;
  /**
   * The portal this account lives on — equals the business mode of the host that
   * published it (HOA host -> Hoa, Company host -> PropertyManagement). Drives
   * both the type badge and which host the cross-host link points at.
   */
  businessMode: CompanyBusinessMode;
};

/**
 * The cookie domain shared by the two portal hosts: the current host minus its
 * first label (`portal.magicdoor-test.com` -> `magicdoor-test.com`). A
 * single-label host (`localhost`, an IP) has no shared parent the browser will
 * accept, so the cookie channel is skipped there and we rely on the mirror.
 */
const sharedCookieDomain = (): string | null => {
  const parts = location.hostname.split('.');
  if (parts.length < 2) return null;
  return parts.slice(1).join('.');
};

/** The business mode of THIS host (HOA host -> Hoa, Company host -> PropertyManagement). */
const THIS_BUSINESS_MODE: CompanyBusinessMode = IS_HOA_DEMO_MODE ? CompanyBusinessMode.Hoa : CompanyBusinessMode.PropertyManagement;

const parseRoster = (raw: string | null | undefined): CrossHostAccount[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CrossHostAccount[]) : [];
  } catch {
    return [];
  }
};

const readCookieRoster = (): CrossHostAccount[] => {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${ROSTER_COOKIE}=`));
  return match ? parseRoster(decodeURIComponent(match.slice(ROSTER_COOKIE.length + 1))) : [];
};

const writeCookieRoster = (roster: CrossHostAccount[]): void => {
  const domain = sharedCookieDomain();
  if (!domain) return;
  try {
    const value = encodeURIComponent(JSON.stringify(roster));
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${ROSTER_COOKIE}=${value}; Domain=${domain}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // Best-effort: cross-host display degrades gracefully to the mirror channel.
  }
};

const readMirrorRoster = (): CrossHostAccount[] => {
  try {
    return parseRoster(localStorage.getItem(ROSTER_MIRROR_KEY));
  } catch {
    return [];
  }
};

const writeMirrorRoster = (roster: CrossHostAccount[]): void => {
  try {
    localStorage.setItem(ROSTER_MIRROR_KEY, JSON.stringify(roster));
  } catch {
    // ignore storage errors — best-effort display
  }
};

/** Last write wins per account id, across both channels. */
const mergeById = (...rosters: CrossHostAccount[][]): CrossHostAccount[] => {
  const byId = new Map<string, CrossHostAccount>();
  for (const roster of rosters) for (const a of roster) byId.set(a.id, a);
  return [...byId.values()];
};

/** Accounts the OTHER host has published, from whichever channel(s) carry them. */
const readOtherHostRoster = (): CrossHostAccount[] =>
  mergeById(readCookieRoster(), readMirrorRoster()).filter((a) => a.businessMode !== THIS_BUSINESS_MODE);

/**
 * Publish THIS host's accounts to the cookie channel, replacing this host's prior
 * entries and leaving the other host's intact. Call whenever this host's account
 * list changes (load, switch, sign-out). The mirror channel is written only by
 * the receiving host on navigation ({@link ingestRosterJson}), so we don't
 * touch it here.
 */
export const syncThisHostAccounts = (accounts: Array<{ id: string; companyId: string; label: string; subLabel: string }>): void => {
  const others = readCookieRoster().filter((a) => a.businessMode !== THIS_BUSINESS_MODE);
  const mine: CrossHostAccount[] = accounts.map((a) => ({
    id: a.id,
    companyId: a.companyId,
    label: a.label,
    subLabel: a.subLabel,
    businessMode: THIS_BUSINESS_MODE,
  }));
  writeCookieRoster([...others, ...mine]);
};

/** This host's accounts, for handing to the other host via the navigation URL. */
const thisHostRoster = (accounts: Array<{ id: string; companyId: string; label: string; subLabel: string }>): CrossHostAccount[] =>
  accounts.map((a) => ({ id: a.id, companyId: a.companyId, label: a.label, subLabel: a.subLabel, businessMode: THIS_BUSINESS_MODE }));

/**
 * This host's roster as a JSON string, for handing to the other host across ANY
 * cross-host navigation (the account-switch link AND the login-time handoff), so
 * the destination can show our accounts even without the shared cookie.
 */
export const serializeThisHostRoster = (accounts: Array<{ id: string; companyId: string; label: string; subLabel: string }>): string =>
  JSON.stringify(thisHostRoster(accounts));

/**
 * Drop THIS host's entries from the cookie channel (e.g. on sign-out), leaving
 * the other host's intact so its switcher still shows them.
 */
export const clearThisHostAccounts = (): void => {
  writeCookieRoster(readCookieRoster().filter((a) => a.businessMode !== THIS_BUSINESS_MODE));
};

/**
 * Replace this origin's mirror with the roster the other host just handed us. The
 * incoming roster is that host's authoritative snapshot at crossing time, so we
 * REPLACE rather than merge — that prunes accounts the user has since removed or
 * signed out of on the other host (a merge would leave them as ghost rows). Only
 * the OTHER host's entries are kept; our own come from the auth store directly.
 * An empty/missing roster is ignored (a no-roster crossing shouldn't wipe what we
 * already know).
 */
export const ingestRosterJson = (rosterJson: string | null | undefined): void => {
  const incoming = parseRoster(rosterJson);
  if (!incoming.length) return;
  writeMirrorRoster(incoming.filter((a) => a.businessMode !== THIS_BUSINESS_MODE));
};

/**
 * Ingest the roster the other host handed us in the URL FRAGMENT (`#…roster=…`).
 * The fragment is never sent to servers/logs — same reason the handoff puts its
 * interaction code there — so the account labels don't leak into access logs.
 */
export const ingestRosterFromHash = (hash: string): void => {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  ingestRosterJson(params.get(ROSTER_PARAM));
};

/**
 * Accounts that live on the OTHER portal host, for display in this host's
 * switcher. `localCompanyIds` are the company ids already present locally (real,
 * switchable rows) so we don't list a company twice when it exists on both hosts.
 */
export const getCrossHostAccounts = (localCompanyIds: Set<string>): CrossHostAccount[] =>
  readOtherHostRoster().filter((a) => !localCompanyIds.has(a.companyId));

/** Route on the target host that activates a specific account then lands on `redirect`. */
export const CROSS_HOST_SWITCH_PATH = '/auth/switch';

/**
 * The URL on the other portal host that activates `account` and lands the user
 * on it. Points at the target's {@link CROSS_HOST_SWITCH_PATH} with the target
 * account id (so it switches to THAT account on arrival, not whatever was active
 * there) and this host's roster (so the target can show our accounts back to us
 * even without the shared cookie). `localAccounts` is this host's current account
 * list, handed over in the FRAGMENT — never sent to servers/logs — so the account
 * labels don't leak into access logs.
 */
export const crossHostAccountUrl = (
  account: CrossHostAccount,
  localAccounts: Array<{ id: string; companyId: string; label: string; subLabel: string }>,
  redirect = '/'
): string => {
  const targetHost =
    account.businessMode === CompanyBusinessMode.Hoa ? toHoaHost(location.hostname) : toPropertyManagementHost(location.hostname);
  const url = new URL(CROSS_HOST_SWITCH_PATH, location.origin);
  url.hostname = targetHost;
  url.searchParams.set('account', account.id);
  url.searchParams.set('redirect', redirect);
  url.hash = `${ROSTER_PARAM}=${encodeURIComponent(JSON.stringify(thisHostRoster(localAccounts)))}`;
  return url.toString();
};
